import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import type {
  AppScopeIdentity,
  ResolvedAppScope
} from 'v5/core/app-scope/appScope.types';

/**
 * Resolves which workspace the enterprise chrome treats as “active”
 * (URL workspace/team, else `fallbackWorkspaceSlug` — the one the chrome showed
 * before an org-level page — else first workspace in the org list).
 *
 * `browsingWorkspaceScope` is that workspace as {@link AppScopeIdentity}
 * (or the original org scope when no workspace is available) — pass it to
 * services that filter by browsing scope (e.g. resource types).
 */
export function resolveActiveEnterpriseWorkspace(
  workspaces: EnterpriseWorkspace[] | undefined,
  scope: ResolvedAppScope,
  fallbackWorkspaceSlug?: string
): {
  activeWorkspace: EnterpriseWorkspace | undefined;
  activeWorkspaceSlug: string;
  activeWorkspaceId: number | undefined;
  browsingWorkspaceScope: AppScopeIdentity;
} {
  const scopeWorkspaceSlug =
    scope.level === 'workspace' || scope.level === 'team'
      ? scope.workspaceSlug
      : undefined;

  const wantedWorkspaceSlug = scopeWorkspaceSlug ?? fallbackWorkspaceSlug;
  const matched =
    wantedWorkspaceSlug && workspaces
      ? workspaces.find(w => w.slug === wantedWorkspaceSlug)
      : undefined;

  const activeWorkspace = matched ?? workspaces?.[0];

  const activeWorkspaceSlug = activeWorkspace?.slug ?? scopeWorkspaceSlug ?? '';
  const activeWorkspaceId =
    activeWorkspace?.id ??
    (scope.level === 'workspace' || scope.level === 'team'
      ? scope.workspaceId
      : undefined);

  const browsingWorkspaceScope: AppScopeIdentity =
    activeWorkspaceId != null
      ? {
          level: 'workspace',
          organizationId: scope.organizationId,
          workspaceId: activeWorkspaceId
        }
      : { level: 'organization', organizationId: scope.organizationId };

  return {
    activeWorkspace,
    activeWorkspaceSlug,
    activeWorkspaceId,
    browsingWorkspaceScope
  };
}
